import { useEffect, useMemo, useState } from "react";
import {
  AzureResponseProps,
  GcpResponseProps,
  ResourceRequestsProps,
} from "../resourceRequestInterface";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import {
  AlertTriangle,
  CheckCircle2,
  CheckCircle2Icon,
  Clock,
  Cloud,
  Eye,
  Layers,
  MessageSquare,
  RotateCcw,
  User,
  XCircle,
} from "lucide-react";
import { changeGCPStatus, getGCPNotificationAPI, getGCPNotificationCountAPI, revokeRequest } from "../Api/resourcerequestapi";
import { format } from "date-fns";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { getStatusColor } from "../utils";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Skeleton } from "../../../ui/skeleton";
import { formatDate } from "../../formatTimeAgo";

export const GCPRequestDetails = ({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  startDate,
  endDate,
  activeTab
}: ResourceRequestsProps) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<GcpResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] =
    useState<GcpResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );
  const { email, role } = useUserStore((state) => state.user);
  const [loadingId, setLoadingId] = useState<number | null>(null);



  const handleRequest = async (
    status: string,
    id: number,
    notification: GcpResponseProps
  ) => {
    setLoadingId(id);
    try {
      await changeGCPStatus(status, id, tenantName, notification.projectId, notification);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null)
    }

    getGCPNotification(false)
  };

  const fetchGCPCount = async () => {
    const response = await getGCPNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role
    )
    setTotalItems(response || 0);
  }

  const getGCPNotification = async (isFirst = true) => {
    setIsLoading(isFirst);
    try {
      const response: GcpResponseProps[] = await getGCPNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleRevoke = async (accessId: number) => {
    await revokeRequest("gcp", accessId)

    setNotifications((prev) =>
      prev.map((r) =>
        r.id === accessId ? { ...r, requestStatus: "REVOKED" } : r
      )
    );
  };

  const handleViewDetails = (request: GcpResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-4 h-4" />;
      case "APPROVED":
        return <CheckCircle2Icon className="w-4 h-4" />;
      case "DECLINED":
        return <XCircle className="w-4 h-4" />;
      case "REVOKED":
        return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  useEffect(() => {
    fetchGCPCount()
    getGCPNotification();
    if (activeTab === "gcp") {
      const interval = setInterval(() => {
        fetchGCPCount()
        getGCPNotification(false);
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  // ✅ Skeleton Loader UI
  const renderSkeleton = () => (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <Card
          key={i}
          className="border-slate-200 dark:border-slate-700 p-4 space-y-3"
        >
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-5 w-20 rounded-md" />
          </div>

          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            {[...Array(4)].map((_, j) => (
              <div key={j}>
                <Skeleton className="h-3 w-24 mb-1" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
            {[...Array(3)].map((_, j) => (
              <div key={j}>
                <Skeleton className="h-3 w-20 mb-1" />
                <Skeleton className="h-4 w-28" />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Skeleton className="h-6 w-20 rounded-md" />
            <Skeleton className="h-6 w-20 rounded-md" />
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <>
      <div className="space-y-1">
        {isLoading ? (
          renderSkeleton()
        ) : notifications.length > 0 ? (
          <>
            {notifications.map((request) => (
              <Card
                key={request.id}
                className="transition-all duration-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
              >
                <CardContent className="p-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Cloud className="w-3.5 h-3.5 text-green-500" />
                              <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                {request.accessName}
                              </h3>
                            </div>
                            <Badge
                              className={`border text-xs px-1.5 py-0.5 ${getStatusColor(
                                request.requestStatus
                              )}`}
                            >
                              <div className="flex items-center gap-1">
                                {getStatusIcon(request.requestStatus)}
                                {request.requestStatus}
                              </div>
                            </Badge>
                          </div>
                          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <User className="w-4 h-4" />
                            {request.email}
                            <span className="text-slate-400">•</span>
                            {formatDate(request.createdAt)}
                          </p>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                          className="h-auto px-2 py-1 text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            GCP User Email
                          </label>
                          <p className="font-medium text-slate-700 dark:text-slate-200">
                            {request.gcpUserEmail}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Role ID
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.roleId}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Resource Type
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.gcpResourceType}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Duration
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.privilegeLevel === "PERMANENT" ? "Permanent" : `${request.selectedTime} min`}

                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 p-3 text-sm border border-green-200 rounded-lg md:grid-cols-3 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
                        <div>
                          <label className="font-medium text-green-600 dark:text-green-400">
                            Project ID
                          </label>
                          <p className="font-mono text-xs text-green-800 dark:text-green-200">
                            {request.projectId}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-green-600 dark:text-green-400">
                            Resource Name
                          </label>
                          <p className="text-xs text-green-800 dark:text-green-200">
                            {request.accessName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <MessageSquare className="w-3 h-3" />
                          {request.requestMessage}
                        </div>
                        <div className="flex items-center gap-2">
                          {request.requestStatus === "PENDING" && (
                            <>
                              {role !== "TENANT_USER" ? (
                                <>
                                  <Button
                                    disabled={loadingId === request.id}

                                    size="sm"
                                    className="text-white bg-green-600 hover:bg-green-700"
                                    onClick={() => handleRequest("APPROVED", request.id, request)}
                                  >
                                    {loadingId === request.id ? (
                                      <>
                                        <svg
                                          className="animate-spin w-4 h-4 mr-1 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                          ></path>
                                        </svg>
                                        Approving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRequest("DECLINE", request.id, request)}>

                                    <XCircle className="w-4 h-4 mr-1" /> Decline
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                                    onClick={() => setIsDetailsOpen?.(false)}
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                                  </Button>
                                  {/**
                                     * <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                    onClick={() => { handleRevoke(request.id) }}>
                                    <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                                  </Button>
                                     */}

                                </>
                              )}

                            </>
                          )}
                          {false && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRevoke(request.id)}
                              className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}

                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-12 text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-green-300 dark:text-green-600" />
              <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">
                No GCP requests found
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No access requests found for GCP resources
              </p>
            </CardContent>
          </Card>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
            </div>

            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                data-testid="button-prev-page-onprem"
              >
                Previous
              </Button>

              {/* Dynamic Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pageNum = startPage + i;

                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                    data-testid={`page-onprem-${pageNum}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              {/* Next Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                data-testid="button-next-page-onprem"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Dialog remains unchanged */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Layers className="w-5 h-5 text-green-500" />
                GCP Access Request Details
              </DialogTitle>
              <DialogDescription>
                Complete information for GCP request #{selectedRequest?.id}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-6 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Request ID</label>
                    <p className="text-slate-700 dark:text-slate-200 font-mono">{selectedRequest.id}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600 dark:text-slate-300">Status:</span>
                    <p className="text-slate-800 dark:text-slate-100">{selectedRequest.requestStatus}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Requested By</label>
                    <p className="text-slate-700 dark:text-slate-200">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">GCP Username</label>
                    <p className="text-slate-700 dark:text-slate-200">{selectedRequest.gcpUserEmail}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Project ID</label>
                    <p className="text-slate-700 dark:text-slate-200 font-mono text-xs">{selectedRequest.projectId}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Access Name</label>
                    <p className="text-slate-700 dark:text-slate-200">{selectedRequest.accessName}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Access Type</label>
                    <p className="text-slate-700 dark:text-slate-200 text-xs">{selectedRequest.accessType}</p>
                  </div>
                  <div>
                    <label className="text-slate-500 dark:text-slate-400 font-medium">Resource Type</label>
                    <p className="text-slate-700 dark:text-slate-200 text-xs">{selectedRequest.gcpResourceType}</p>
                  </div>

                </div>

                <div>
                  <label className="text-slate-500 dark:text-slate-400 font-medium">Business Justification</label>
                  <p className="text-slate-700 dark:text-slate-200 mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    {selectedRequest.requestMessage}
                  </p>
                </div>

                {/* Admin Action Buttons */}
                {selectedRequest.requestStatus === "PENDING" && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setIsDetailsOpen(false)}
                    >
                      Close
                    </Button>

                    {role === "TENANT_USER" ? <Button
                      variant="destructive"
                      onClick={() => {
                        setIsDetailsOpen(false);
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Pending
                    </Button> : <>

                      <Button
                        variant="destructive"
                        onClick={() => {
                          handleRequest("DECLINED", selectedRequest.id, selectedRequest);
                          setIsDetailsOpen(false);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Decline
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          handleRequest("APPROVED", selectedRequest.id, selectedRequest)
                          setIsDetailsOpen(false);
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>

                    </>}


                  </div>
                )}

                {selectedRequest.requestStatus === "APPROVED" && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setIsDetailsOpen(false)}
                    >
                      Close
                    </Button>
                    {/**
                     * <Button
                      variant="outline"
                      className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                      onClick={() => {
                        handleRevoke(selectedRequest.id);
                        setIsDetailsOpen(false);
                      }}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Revoke
                    </Button>
                     */}

                  </div>
                )}

                {(selectedRequest.requestStatus === "DECLINED" || selectedRequest.requestStatus === "REVOKED" || selectedRequest.requestStatus === "EXPIRED") && (
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setIsDetailsOpen(false)}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
